/**
 * DELETE ONBOARDING LOCATION BUTTON COMPONENT
 *
 * System admin button to delete an onboarding location before activation.
 * Shows confirmation modal with information about deconfig tasks that will be created.
 * Disabled when organization has only one location.
 */

'use client';

import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import type { Location } from '@/types/database';

interface DeleteOnboardingLocationButtonProps {
  organizationId: string;
  location: Location;
  userRole: string;
  onDeleteSuccess?: () => void;
}

export function DeleteOnboardingLocationButton({
  organizationId,
  location,
  userRole,
  onDeleteSuccess,
}: DeleteOnboardingLocationButtonProps) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canDelete, setCanDelete] = useState(false);
  const [locationCount, setLocationCount] = useState(0);
  const [deletionInfo, setDeletionInfo] = useState<{
    needsITDeconfig: boolean;
    needsDataDeconfig: boolean;
    deletionStage: string;
  } | null>(null);
  const [open, setOpen] = useState(false);

  const supabase = createClient();

  // Only system admins can delete locations
  if (userRole !== 'SYSTEM_ADMIN') {
    return null;
  }

  // Check if location can be deleted
  useEffect(() => {
    const checkCanDelete = async () => {
      try {
        setChecking(true);

        // Check if location is activated
        const { data: activationTask } = await supabase
          .from('tasks')
          .select('id, status')
          .eq('location_id', location.id)
          .eq('type', 'FINAL_ACTIVATION')
          .maybeSingle();

        if (activationTask?.status === 'COMPLETED') {
          setError('Cannot delete an activated location. Use the deactivation workflow instead.');
          setCanDelete(false);
          setChecking(false);
          return;
        }

        // Check if this is the last location
        const { data: allLocations, error: locationsError } = await supabase
          .from('locations')
          .select('id')
          .eq('organization_id', organizationId);

        if (locationsError) throw locationsError;

        const count = allLocations?.length || 0;
        setLocationCount(count);

        if (count <= 1) {
          setError('Cannot delete the last location for an organization.');
          setCanDelete(false);
          setChecking(false);
          return;
        }

        // Determine what deconfig tasks would be needed
        const { data: existingTasks } = await supabase
          .from('tasks')
          .select('id, type, status')
          .eq('location_id', location.id)
          .eq('organization_id', organizationId);

        const completedITProvisioning = existingTasks?.some(
          (task) => task.type === 'IT_PROVISIONING' && task.status === 'COMPLETED'
        );
        const completedDataConfiguration = existingTasks?.some(
          (task) => task.type === 'DATA_CONFIGURATION' && task.status === 'COMPLETED'
        );

        // Flow is sequential: IT → DATA → Activation
        // So we only have 3 scenarios:
        // 1. Both IT and DATA completed
        // 2. Only IT completed
        // 3. Neither completed
        let stage = 'DELETED';
        let needsIT = false;
        let needsData = false;

        if (completedITProvisioning && completedDataConfiguration) {
          stage = 'AWAITING_BOTH_DECONFIG';
          needsIT = true;
          needsData = true;
        } else if (completedITProvisioning) {
          stage = 'AWAITING_IT_DECONFIG';
          needsIT = true;
        }

        setDeletionInfo({
          needsITDeconfig: needsIT,
          needsDataDeconfig: needsData,
          deletionStage: stage,
        });

        setCanDelete(true);
        setChecking(false);
      } catch (err) {
        console.error('Error checking delete eligibility:', err);
        setError('Failed to check if location can be deleted');
        setCanDelete(false);
        setChecking(false);
      }
    };

    checkCanDelete();
  }, [organizationId, location.id, supabase]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the edge function to delete the location
      const { data, error: functionError } = await supabase.functions.invoke(
        'delete-onboarding-location',
        {
          body: {
            organization_id: organizationId,
            location_id: location.id,
          },
        }
      );

      if (functionError) throw functionError;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete location');
      }

      // Success
      setOpen(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete location');
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Button disabled size="sm" variant="destructive">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Checking...
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          disabled={!canDelete || loading}
          size="sm"
          variant="destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Location
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Delete Onboarding Location
          </DialogTitle>
          <DialogDescription className="space-y-4">
            <p>
              Are you sure you want to delete <strong>{location.name}</strong>?
              This action cannot be undone.
            </p>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded-md">
                {error}
              </div>
            )}

            {deletionInfo && (
              <div className="bg-muted p-3 rounded-md space-y-2">
                <p className="text-sm font-semibold">What will happen:</p>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>All pending onboarding tasks will be deleted</li>
                  {deletionInfo.needsITDeconfig && (
                    <li className="text-orange-600 dark:text-orange-400">
                      <strong>IT Deconfiguration</strong> task will be created
                    </li>
                  )}
                  {deletionInfo.needsDataDeconfig && (
                    <li className="text-orange-600 dark:text-orange-400">
                      <strong>Data Deconfiguration</strong> task will be created
                    </li>
                  )}
                  {!deletionInfo.needsITDeconfig && !deletionInfo.needsDataDeconfig && (
                    <li className="text-green-600 dark:text-green-400">
                      Location will be deleted immediately (no deconfig needed)
                    </li>
                  )}
                  <li>Location will be removed from the main application</li>
                </ul>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              {locationCount > 1
                ? `This organization has ${locationCount} locations. Deleting this location will leave ${locationCount - 1} remaining.`
                : 'Loading location count...'}
            </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={loading || !canDelete}
            variant="destructive"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Location
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
